import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { useActionHook, useModals } from "hooks";
import { useDealActions, useDealAssetClassSelect, useDealIdSelector } from "store";
import { IAssetClass, IDeal } from "store/types";

interface IAsset {
  assetClass?: IAssetClass;
  close: () => void;
}

export const Content: React.FC<IAsset> = ({ assetClass, close }) => {
  const { updateDealAction, getAssetClassesAction } = useDealActions();
  const { confirmModal } = useModals();

  const dealId = useDealIdSelector();
  const dealAssetClass = useDealAssetClassSelect();

  const { data = [], action: getAssetClasses, loading: assetClassesLoading } = useActionHook(getAssetClassesAction);

  useEffect(() => {
    getAssetClasses();
  }, []);

  const [value, setValue] = useState<IAssetClass | undefined>(assetClass);

  const { action, loading } = useActionHook(updateDealAction, {
    onSuccess: (res: IDeal) => {
      if (res.confirm_change && value && dealId) {
        confirmModal({
          msg: `There are measure definitions with the same name and type on new asset class level and on the current deal level. In order to proceed with the asset class change we need to unite these measure to Asset Class level. 
                    Would you like to continue with Asset Class change?`,
          title: "Change Asset Class",
          action: async () => action({ dealId, asset_class_id: value.id, confirm: true }).then(close)
        });
      } else {
        close();
      }
    },
    onReject: (e) => {
      setValue(dealAssetClass);
      toast.error(e.message);
    }
  });

  const isDisabled: boolean = !value || value.id === dealAssetClass?.id || loading;

  const onSubmit = useCallback(async () => {
    if (!dealId || !value) return;
    action({ dealId, asset_class_id: value.id });
  }, [action, value, dealId]);

  if (!value) return null;

  return (
    <ProgressOverlay
      inProgress={assetClassesLoading}
      containerSX={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "20px",
        width: "520px",
        padding: "20px 20px 20px 25px",
        boxShadow: "6px -3px 4px rgba(0, 0, 0, 0.25)"
      }}
    >
      <Autocomplete
        fullWidth
        options={data}
        value={value}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IAssetClass) => (
          <li {...props} key={option.id}>
            {option.account_alias || "N/A"}
          </li>
        )}
        getOptionLabel={(option) => option.account_alias || "N/A"}
        onChange={(_, option) => setValue(option)}
        disableClearable
        renderInput={(params) => <TextField label="Asset Classes" {...params} />}
      />
      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
        <Button variant="outlined" onClick={close}>
          Cancel
        </Button>
        <Button disabled={isDisabled} onClick={onSubmit} variant="contained">
          Apply
        </Button>
      </Stack>
    </ProgressOverlay>
  );
};
